import { useEffect, useMemo, useState } from 'react'
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded'
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded'
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import { useAuth } from '../../context/useAuth'
import { useGamification } from '../../context/useGamification'
import type { DisplayPreferenceType } from '../../types/schema'
import { AVATARS, DEFAULT_UNLOCKS } from '../../utils/cosmeticCatalog'
import {
  computeDisplayName,
  helperUsernameFor,
  normalizeAccountRole,
  normalizeUsername,
  validateUsername,
} from '../../utils/userIdentity'
import { resolveProfileImageUrl } from '../../utils/profileImage'
import { updateUserProfile, updateUserProfileImage } from '../../services/userProfileService'
import { AppDialog } from '../Common/AppDialog'
import { Select, TextInput } from '../Common'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { Card } from '../Common/Card'
import { DeleteAccountModal } from '../DeleteAccountModal'
import { ProfileDetailsPage } from './ProfileDetailsPage'
import { isHelperOnlyAccount } from '../../utils/helperMembership'
import { MoveToGoogleAccountModal } from '../AccountTransfer/MoveToGoogleAccountModal'
import { ClaimTransferCodeModal } from '../AccountTransfer/ClaimTransferCodeModal'

type ProfileDetailsPanelProps = {
  variant?: 'member' | 'helper'
  onOpenNotificationsSettings?: () => void
}

type ProfileIdentityCardProps = {
  variant?: 'member' | 'helper'
}

const displayTypeOptions: Array<{ value: DisplayPreferenceType; label: string }> = [
  { value: 'FIRST_LAST', label: 'First + last' },
  { value: 'USERNAME', label: 'Username' },
  { value: 'PHONE', label: 'Phone' },
]

export const ProfileIdentityCard = ({ variant = 'member' }: ProfileIdentityCardProps) => {
  const { currentUser, userProfile } = useAuth()
  const { profile: gamificationProfile } = useGamification()
  const accountRole = normalizeAccountRole(userProfile?.role)
  const isHelper = variant === 'helper' || isHelperOnlyAccount({ userProfile, currentUser })
  const helperInternalId = userProfile?.helperMetadata?.internalUid || currentUser?.uid || ''
  const lockedHelperUsername = helperUsernameFor(helperInternalId)
  const lockedHelperPhone = userProfile?.helperMetadata?.mobileNumber || userProfile?.phone || currentUser?.email || ''
  const [isEditing, setIsEditing] = useState(false)
  const [firstName, setFirstName] = useState(userProfile?.firstName ?? '')
  const [lastName, setLastName] = useState(userProfile?.lastName ?? '')
  const [username, setUsername] = useState(isHelper ? lockedHelperUsername : userProfile?.username ?? '')
  const [displayType, setDisplayType] = useState<DisplayPreferenceType>(
    userProfile?.displayPreferences?.type ?? (isHelper ? 'PHONE' : 'FIRST_LAST'),
  )
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const [imageSource, setImageSource] = useState<'GOOGLE' | 'AVATAR'>(
    userProfile?.profileImage?.source === 'AVATAR' ? 'AVATAR' : 'GOOGLE',
  )
  const [selectedAvatarId, setSelectedAvatarId] = useState(
    userProfile?.profileImage?.avatarId || gamificationProfile?.equippedAvatarId || DEFAULT_UNLOCKS.avatars[0],
  )
  const [imageBusy, setImageBusy] = useState(false)
  const [imageMessage, setImageMessage] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [moveModalOpen, setMoveModalOpen] = useState(false)
  const [claimModalOpen, setClaimModalOpen] = useState(false)
  const canMoveAccount = !isHelper && Boolean(currentUser && !currentUser.isAnonymous)
  const canClaimTransfer = Boolean(currentUser && !currentUser.isAnonymous && !isHelper)

  useEffect(() => {
    if (isEditing) return
    setFirstName(userProfile?.firstName ?? '')
    setLastName(userProfile?.lastName ?? '')
    setUsername(isHelper ? lockedHelperUsername : userProfile?.username ?? '')
    setDisplayType(userProfile?.displayPreferences?.type ?? (isHelper ? 'PHONE' : 'FIRST_LAST'))
    setImageSource(userProfile?.profileImage?.source === 'AVATAR' ? 'AVATAR' : 'GOOGLE')
    setSelectedAvatarId(userProfile?.profileImage?.avatarId || gamificationProfile?.equippedAvatarId || DEFAULT_UNLOCKS.avatars[0])
  }, [gamificationProfile?.equippedAvatarId, isEditing, isHelper, lockedHelperUsername, userProfile])

  const normalizedUsername = isHelper ? lockedHelperUsername : normalizeUsername(username)
  const usernameError = validateUsername(normalizedUsername)
  const canSave = Boolean(currentUser) && !usernameError && !saving
  const computedName = useMemo(() => computeDisplayName({
    firstName,
    lastName,
    username: normalizedUsername,
    phone: isHelper ? lockedHelperPhone : userProfile?.phone ?? '',
    email: currentUser?.email ?? userProfile?.email ?? '',
    displayPreferences: { type: displayType },
  }), [currentUser?.email, displayType, firstName, isHelper, lastName, lockedHelperPhone, normalizedUsername, userProfile?.email, userProfile?.phone])
  const profileImageUrl = resolveProfileImageUrl(userProfile ?? undefined, currentUser)
  const displayInitial = (computedName || 'U').trim().charAt(0).toUpperCase() || 'U'
  const ownedAvatarIds = useMemo(() => new Set([
    ...DEFAULT_UNLOCKS.avatars,
    ...(gamificationProfile?.unlockedAvatarIds ?? []),
    ...(gamificationProfile?.equippedAvatarId ? [gamificationProfile.equippedAvatarId] : []),
  ]), [gamificationProfile?.equippedAvatarId, gamificationProfile?.unlockedAvatarIds])
  const availableAvatars = useMemo(
    () => AVATARS.filter((avatar) => ownedAvatarIds.has(avatar.id)),
    [ownedAvatarIds],
  )
  const usernameLocked = isHelper || Boolean(userProfile?.username)

  const resetDraft = () => {
    setFirstName(userProfile?.firstName ?? '')
    setLastName(userProfile?.lastName ?? '')
    setUsername(isHelper ? lockedHelperUsername : userProfile?.username ?? '')
    setDisplayType(userProfile?.displayPreferences?.type ?? (isHelper ? 'PHONE' : 'FIRST_LAST'))
    setMessage(null)
    setIsEditing(false)
  }

  const saveIdentity = async () => {
    if (!canSave) return
    setSaving(true)
    setMessage(null)
    try {
      await updateUserProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        username: normalizedUsername,
        phone: isHelper ? lockedHelperPhone : userProfile?.phone ?? '',
        address: userProfile?.address ?? undefined,
        educationNotes: userProfile?.educationNotes ?? undefined,
        displayType,
      })
      setMessage('Profile identity saved.')
      setIsEditing(false)
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Profile identity could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  const openImageDialog = () => {
    setImageSource(userProfile?.profileImage?.source === 'AVATAR' ? 'AVATAR' : 'GOOGLE')
    setSelectedAvatarId(userProfile?.profileImage?.avatarId || gamificationProfile?.equippedAvatarId || DEFAULT_UNLOCKS.avatars[0])
    setImageMessage(null)
    setImageDialogOpen(true)
  }

  const saveProfileImage = async () => {
    setImageBusy(true)
    setImageMessage(null)
    try {
      await updateUserProfileImage(imageSource === 'GOOGLE' ? { source: 'GOOGLE' } : { source: 'AVATAR', avatarId: selectedAvatarId })
      setImageDialogOpen(false)
    } catch (error) {
      setImageMessage(error instanceof Error ? error.message : 'Profile picture could not be updated.')
    } finally {
      setImageBusy(false)
    }
  }

  if (!currentUser) return null

  return (
    <>
      <Card
        className="settingsWideField profileIdentityCard"
        variant="glass"
        accent="primary"
        icon={<AccountCircleRoundedIcon fontSize="inherit" />}
        eyebrow="Profile"
        title="Identity"
        subtitle="Your avatar, display name, username, and account entry points."
        actions={isEditing ? (
          <div className="settingsPanelActionRow">
            <Button variant="tertiary" size="sm" icon={<CloseRoundedIcon />} disabled={saving} onClick={resetDraft}>Cancel</Button>
            <Button variant="primary" size="sm" icon={<SaveRoundedIcon />} loading={saving} disabled={!canSave} onClick={() => void saveIdentity()}>
              {saving ? 'Saving' : 'Save'}
            </Button>
          </div>
        ) : (
          <Button variant="secondary" size="sm" icon={<PersonRoundedIcon />} onClick={() => setIsEditing(true)}>Edit</Button>
        )}
      >
        <div className="profileIdentityLayout">
          <Pressable
            className="profileDetailsAvatar profileDetailsAvatarButton profileIdentityAvatar"
            onClick={openImageDialog}
            aria-label="Change profile picture"
          >
            {profileImageUrl ? <img src={profileImageUrl} alt="" /> : displayInitial}
          </Pressable>
          <div className="profileIdentityMain">
            <strong>{computedName}</strong>
            <span>{accountRole}</span>
            <Button variant="tertiary" size="sm" icon={<PhotoCameraRoundedIcon />} onClick={openImageDialog}>Change picture</Button>
          </div>
        </div>

        {isEditing ? (
          <div className="profileOnboardingGrid profileDetailsGrid profileIdentityForm">
            <label><span>First name</span><TextInput value={firstName} onChange={setFirstName} autoComplete="given-name" /></label>
            <label><span>Last name</span><TextInput value={lastName} onChange={setLastName} autoComplete="family-name" /></label>
            <label>
              <span>Username</span>
              <TextInput value={normalizedUsername} disabled={usernameLocked} readOnly={usernameLocked} onChange={setUsername} autoComplete="username" />
              <small>{usernameLocked ? 'Username is permanent once assigned.' : 'Lowercase letters, numbers, and hyphens.'}</small>
            </label>
            <label>
              <span>Display as</span>
              <Select<DisplayPreferenceType>
                ariaLabel="Display preference"
                value={displayType}
                options={displayTypeOptions}
                onChange={setDisplayType}
                trigger="pill"
              />
            </label>
          </div>
        ) : (
          <div className="profileDetailsReadout profileIdentityReadout" aria-label="Profile identity details">
            <div><span>Display name</span><strong>{computedName}</strong></div>
            <div><span>Username</span><strong>{userProfile?.username || normalizedUsername || 'Not set'}</strong></div>
            <div><span>Email</span><strong>{currentUser.email ?? userProfile?.email ?? 'Not set'}</strong></div>
            <div><span>Phone</span><strong>{userProfile?.helperMetadata?.mobileNumber || userProfile?.phone || 'Not set'}</strong></div>
            <div><span>Display preference</span><strong>{userProfile?.displayPreferences?.type ?? displayType}</strong></div>
          </div>
        )}

        {usernameError && isEditing ? <p className="authError">{usernameError}</p> : null}
        {message ? <p className="profileDetailsMessage">{message}</p> : null}

        <div className="settingsPanelActionRow profileIdentityDangerRow">
          <span className="settingsFieldHint">
            <EmailRoundedIcon fontSize="inherit" /> {currentUser.email ?? userProfile?.email ?? 'No email'}
            <PhoneRoundedIcon fontSize="inherit" /> {userProfile?.helperMetadata?.mobileNumber || userProfile?.phone || 'No phone'}
            <BadgeRoundedIcon fontSize="inherit" /> {helperInternalId || currentUser.uid}
          </span>
          {canMoveAccount && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setMoveModalOpen(true)}
            >
              Move to another Google account
            </Button>
          )}
          {canClaimTransfer && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setClaimModalOpen(true)}
            >
              Have a transfer code?
            </Button>
          )}
          <Button variant="secondary" size="sm" icon={<DeleteForeverRoundedIcon />} onClick={() => setDeleteOpen(true)}>
            Delete account
          </Button>
        </div>
      </Card>

      <AppDialog
        open={imageDialogOpen}
        title="Change profile picture"
        onDismiss={() => { if (!imageBusy) setImageDialogOpen(false) }}
        message={(
          <div className="profileImageDialogBody">
            <div className="profileImagePreview" aria-hidden="true">
              {imageSource === 'GOOGLE' && currentUser.photoURL
                ? <img src={currentUser.photoURL} alt="" />
                : <img src={resolveProfileImageUrl({ profileImage: { source: 'AVATAR', avatarId: selectedAvatarId } })} alt="" />}
            </div>
            <div className="profileImageSourceGrid">
              <Pressable
                aria-pressed={imageSource === 'GOOGLE'}
                className={`profileImageSourceCard ${imageSource === 'GOOGLE' ? 'isActive' : ''}`}
                onClick={() => setImageSource('GOOGLE')}
                disabled={imageBusy || !currentUser.photoURL}
              >
                <strong>Google image</strong>
                <span>{currentUser.photoURL ? 'Sync from your Google/Gmail account.' : 'No Google image is available.'}</span>
              </Pressable>
              <Pressable
                aria-pressed={imageSource === 'AVATAR'}
                className={`profileImageSourceCard ${imageSource === 'AVATAR' ? 'isActive' : ''}`}
                onClick={() => setImageSource('AVATAR')}
                disabled={imageBusy || availableAvatars.length === 0}
              >
                <strong>Timelining avatar</strong>
                <span>Use one of your available avatars.</span>
              </Pressable>
            </div>
            {imageSource === 'AVATAR' ? (
              <div className="profileAvatarChoiceGrid">
                {availableAvatars.map((avatar) => (
                  <Pressable
                    key={avatar.id}
                    aria-pressed={selectedAvatarId === avatar.id}
                    className={`profileAvatarChoice ${selectedAvatarId === avatar.id ? 'isActive' : ''}`}
                    onClick={() => setSelectedAvatarId(avatar.id)}
                    disabled={imageBusy}
                  >
                    <span aria-hidden="true">{avatar.emoji}</span>
                    <strong>{avatar.name}</strong>
                  </Pressable>
                ))}
              </div>
            ) : null}
            {imageMessage ? <p className="profileDetailsMessage">{imageMessage}</p> : null}
          </div>
        )}
        actions={[
          { label: 'Cancel', variant: 'ghost', onClick: () => setImageDialogOpen(false), disabled: imageBusy },
          { label: 'Use this image', variant: 'primary', onClick: saveProfileImage, disabled: imageBusy || (imageSource === 'GOOGLE' && !currentUser.photoURL), loading: imageBusy },
        ]}
      />

      {deleteOpen ? <DeleteAccountModal userId={currentUser.uid} onClose={() => setDeleteOpen(false)} /> : null}

      {moveModalOpen ? (
        <MoveToGoogleAccountModal
          open={moveModalOpen}
          onClose={() => setMoveModalOpen(false)}
        />
      ) : null}

      {claimModalOpen ? (
        <ClaimTransferCodeModal
          open={claimModalOpen}
          onClose={() => setClaimModalOpen(false)}
        />
      ) : null}
    </>
  )
}

export const ProfileDetailsPanel = ({ variant = 'member', onOpenNotificationsSettings }: ProfileDetailsPanelProps) => (
  <ProfileDetailsPage variant={variant} onOpenNotificationsSettings={onOpenNotificationsSettings} />
)
